/**
 * Hôte du tiroir de fiche (voir formDrawer.ts) : monté une fois dans ClientProviders, à côté du copilote.
 * Affiche, dans un RightDrawer large, le composant de page résolu pour le chemin demandé, en mode intégré ;
 * « Ouvrir en page » bascule vers la navigation classique ; tout changement de route ferme le tiroir.
 */
import React, { Suspense, useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router';
import RightDrawer from '@/uptk/core/components/ui/RightDrawer';
import {
  closeFormDrawer,
  resolveFormDrawerComponent,
  splitFormDrawerPath,
  useFormDrawerState,
  type EmbeddedFormSavedMeta,
} from './formDrawer';

const OPEN_PAGE_CLASS =
  'rounded-lg border border-portal-border bg-portal-surface px-3 py-1.5 text-xs font-medium text-portal-ink transition hover:bg-portal-surface-muted';

export default function FormDrawerHost() {
  const drawer = useFormDrawerState();
  const location = useLocation();
  const navigate = useNavigate();
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });

  // Une navigation (depuis la fiche ou ailleurs) ferme le tiroir : la page de destination prend le relais.
  const lastPathname = useRef(location.pathname);
  useEffect(() => {
    if (lastPathname.current !== location.pathname) {
      lastPathname.current = location.pathname;
      if (drawer.open) closeFormDrawer();
    }
  }, [location.pathname, drawer.open]);

  const handleSaved = useCallback(
    (record?: unknown, meta?: EmbeddedFormSavedMeta) => {
      drawer.onSaved?.(record, meta);
      closeFormDrawer();
    },
    [drawer]
  );

  const openAsPage = useCallback(() => {
    const target = drawer.path;
    closeFormDrawer();
    void navigate(target);
  }, [drawer.path, navigate]);

  if (!drawer.open) return null;
  const Component = resolveFormDrawerComponent(drawer.path);
  if (!Component) return null;
  const { pathname, search } = splitFormDrawerPath(drawer.path);

  return (
    <div data-print-hide="true" className="contents">
      <RightDrawer
        isOpen
        onClose={closeFormDrawer}
        title={drawer.title ?? t('core.formDrawer.title')}
        size="xl"
        headerActions={
          <button type="button" className={OPEN_PAGE_CLASS} onClick={openAsPage}>
            {t('core.formDrawer.openPage')}
          </button>
        }
      >
        <Suspense fallback={<p className="text-sm text-portal-ink-muted">{t('core.formDrawer.loading')}</p>}>
          <Component
            key={drawer.key}
            embedded
            pathname={pathname}
            search={search}
            onSaved={handleSaved}
            onCancel={closeFormDrawer}
          />
        </Suspense>
      </RightDrawer>
    </div>
  );
}
